import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "schema/data/resource";
import { CheckIcon } from "assets/icons";
import { GeneralSelectInput } from "components/molecules";
import { useUserData } from "hooks";
import "./GSAppLinkSection.scss";

const client = generateClient<Schema>();

interface AppOption {
  label: string;
  value: string;
}

const GSAppLinkSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { userData } = useUserData();
  const [appOptions, setAppOptions] = useState<AppOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApplications = async () => {
      if (!userData?.id) return;

      try {
        setLoading(true);
        const result = await client.models.Application.list({
          filter: {
            userId: { eq: userData.id },
          },
        });

        if (result.data && result.data.length > 0) {
          const options: AppOption[] = result.data.map((app) => ({
            label: `${app.applicationId} - ${app.status || "Draft"}`,
            value: app.applicationId,
          }));

          setAppOptions(options);
        } else {
          setAppOptions([]);
        }
      } catch (err) {
        console.error("Failed to fetch applications:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [userData?.id]);

  if (loading) {
    return (
      <div className="app-link-section">
        <div className="loading">Loading applications...</div>
      </div>
    );
  }

  return (
    <div className="app-link-section">
      <div className="section-header">
        <h3>Link to Application</h3>
        <p>
          If you've already started an application, you can link this Genuine
          Student Assessment to it.
        </p>
      </div>

      <div className="form-fields">
        <GeneralSelectInput
          id="applicationId"
          label="Select Application"
          options={[
            { label: "None - Skip this step", value: "" },
            ...appOptions,
          ]}
          value={values.applicationId || ""}
          onChange={(e) => setFieldValue("applicationId", e.target.value)}
          onBlur={() => {}}
          disabled={appOptions.length === 0}
        />

        {appOptions.length === 0 && (
          <p className="info-text">
            No applications found. You can skip this step.
          </p>
        )}

        {values.applicationId && (
          <div className="success-indicator">
            <CheckIcon />
            <span>
              This Genuine Student Assessment will be linked to your selected
              application.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GSAppLinkSection;
